import { defineComponent as _defineComponent } from 'vue'
import { trackEvent } from '@@/bits/analytics'
import { subscribeUpdateBodySizeCss, unsubscribeUpdateBodySizeCss } from '@@/bits/body_sizing'
import {
  PasteType,
  extractPostAttributesArrayFromClipboardData,
  extractPostAttributesFromClipboardData,
  removeAttachmentFromPostAttributes,
} from '@@/bits/clipboard'
import device from '@@/bits/device'
import { isActiveElementAnInputField, isEventOnAnInputField } from '@@/bits/dom'
import { isAppUsing } from '@@/bits/flip'
import window from '@@/bits/global'
import { __ } from '@@/bits/intl'
import { navigateTo } from '@@/bits/location'
import loadMapApi from '@@/bits/map_api_loader'
import safeStorage from '@@/bits/safe_storage'
import { isNavigatedFromSubmissionRequest } from '@@/bits/surface_share_links_helper'
import { defineAsyncComponent, stableSync } from '@@/bits/vue'
import { useContentPickerContainerSizeStore } from '@@/pinia/content_picker_container_size'
import type { GlobalSnackbarNotification } from '@@/pinia/global_snackbar'
import { useGlobalSnackbarStore } from '@@/pinia/global_snackbar'
import { SEARCH_BAR_DEFAULT_TOP, useSurfaceStore } from '@@/pinia/surface'
import { useCommentsStore } from '@@/pinia/surface_comments'
import { useSurfaceContainerSizeStore } from '@@/pinia/surface_container_size'
import { useSurfaceDraftsStore } from '@@/pinia/surface_drafts'
import { useSurfaceMapStore } from '@@/pinia/surface_map'
import { useSurfaceOnboardingPanelStore } from '@@/pinia/surface_onboarding_panel'
import { useSurfacePermissionsStore } from '@@/pinia/surface_permissions'
import { useSurfacePostPropertiesStore } from '@@/pinia/surface_post_properties'
import { useSurfacePostsStore } from '@@/pinia/surface_posts'
import { useReactionsStore } from '@@/pinia/surface_reactions'
import { useSurfaceShareLinksStore } from '@@/pinia/surface_share_links'
import { useSurfaceStartingStateStore } from '@@/pinia/surface_starting_state'
import { useSurfaceWishArrangementStore } from '@@/pinia/surface_wish_arrangement'
import type { Id } from '@@/types'
import { useNativeBridgeListeners } from '@@/vuecomposables/native_bridge_listeners'
import { useDoubleClickToPost } from '@@/vuecomposables/surface_double_click_to_post'
import { useSurfaceFetchInitialState } from '@@/vuecomposables/surface_fetch_initial_state'
import { isEmpty } from '@@/vuexstore/helpers/post'
import { GroupByTypes } from '@padlet/arvo'
import { storeToRefs } from 'pinia'
import type { KeyBindingMap } from 'tinykeys'
import tinykeys from 'tinykeys'
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'


export default /*#__PURE__*/_defineComponent({
  __name: 'SurfaceContainer',
  setup(__props) {

const SurfaceActionBar = defineAsyncComponent(() => import('@@/vuecomponents/SurfaceActionBar.vue'))
const SurfaceGuestIdLabel = defineAsyncComponent(() => import('@@/vuecomponents/SurfaceGuestIdLabel.vue'))
const SurfaceContainerHeaderPad = defineAsyncComponent(() => import('@@/vuecomponents/SurfaceContainerHeaderPad.vue'))
const SurfaceContainerGridMasonry = defineAsyncComponent(
  () => import('@@/vuecomponents/SurfaceContainerGridMasonry.vue'),
)
const SurfaceContainerGrid = defineAsyncComponent(() => import('@@/vuecomponents/SurfaceContainerGrid.vue'))
const SurfaceContainerMap = defineAsyncComponent(() => import('@@/vuecomponents/surface_container_map.vue'))
const SurfaceContainerTimelineOld = defineAsyncComponent(
  () => import('@@/vuecomponents/surface_container_timeline.vue'),
)
const SurfaceContainerTimeline = defineAsyncComponent(() => import('@@/vuecomponents/SurfaceContainerTimeline.vue'))
const SurfaceContainerMatrix = defineAsyncComponent(() => import('@@/vuecomponents/SurfaceContainerMatrix.vue'))
const SurfaceContainerStream = defineAsyncComponent(() => import('@@/vuecomponents/SurfaceContainerStream.vue'))
const SurfaceContainerCanvas = defineAsyncComponent(() => import('@@/vuecomponents/SurfaceContainerCanvas.vue'))
const SurfaceContainerSectionBreakout = defineAsyncComponent(
  () => import('@@/vuecomponents/SurfaceContainerSectionBreakout.vue'),
)
const SurfaceContainerSubmissionRequest = defineAsyncComponent(
  () => import('@@/vuecomponents/SurfaceContainerSubmissionRequest.vue'),
)
const SurfaceAddPost = defineAsyncComponent(() => import('@@/vuecomponents/SurfaceAddPost.vue'))
const SurfacePanels = defineAsyncComponent(() => import('@@/vuecomponents/SurfacePanels.vue'))
const SurfaceMagicWallFeedbackPopup = defineAsyncComponent(
  () => import('@@/vuecomponents/SurfaceMagicWallFeedbackPopup.vue'),
)

const isMapApiLoaded = ref(false)
const isApp = device.app
const wallContainerMainElementRef = ref<HTMLDivElement | undefined>(undefined)
const reactionIdsByPostId = ref<Record<Id, Id[]>>({})
const commentIdsByPostId = ref<Record<Id, Id[]>>({})
const headerBackgroundTop = ref<number>(0)
const pageScrollHandlerTicking = ref<boolean>(false)
const isWallCreatedWithAi = ref<boolean>(false)

const { googleApiKeyForMap, currentCountryCode } = storeToRefs(useSurfaceStartingStateStore())
const { amIOwner, canIPost, amIRegistered } = storeToRefs(useSurfacePermissionsStore())
const surfaceStore = useSurfaceStore()
const {
  isEmbedded,
  isOnline,
  format,
  isOverlayVisible,
  isCanvas,
  isGrid,
  isMap,
  isScreenshotMode,
  isStream,
  isTable,
  isTimeline,
  isTimelineV1,
  isTimelineV2,
  isMatrix,
  isMobileLayout,
  isDesktopLayout,
  xHeader,
  isSubscriptionPaused,
  canUseSections,
  headerHeight,
  headerColor,
  isSectionBreakout,
  isSubmissionRequest,
  isFrozen,
  hasSidePanelOutsideSurface,
  user,
  isSearchBarInEditMode,
} = storeToRefs(surfaceStore)
const { refresh, resizeHeader } = surfaceStore
const { isContainerSmallerThanTabletLandscape, isContainerSmallerThanTabletPortrait } = storeToRefs(
  useSurfaceContainerSizeStore(),
)
const sufacePostsStore = useSurfacePostsStore()
const { postBeingEditedCid } = storeToRefs(sufacePostsStore)
const { startNewPost } = sufacePostsStore
const { wishGroupBy } = storeToRefs(useSurfaceWishArrangementStore())
const { reactionIdsByPost } = storeToRefs(useReactionsStore())
const { commentIdsByPost } = storeToRefs(useCommentsStore())
const { setSnackbar } = useGlobalSnackbarStore()
const { startPickingLocation } = useSurfaceMapStore()
const { startNewDraft } = useSurfaceDraftsStore()
const { showPostPublishedSnackbar } = useSurfaceShareLinksStore()
const { observeSurfaceContainerSize } = useSurfaceContainerSizeStore()
const { observeContainerSize } = useContentPickerContainerSizeStore()
const { shouldHandlePasteToAddAttachment } = storeToRefs(useSurfacePostPropertiesStore())
const { setIsActionBarMounted } = useSurfaceOnboardingPanelStore()

// Getters
const isSidePanelDesktop = computed<boolean>(() => {
  return hasSidePanelOutsideSurface.value && !isContainerSmallerThanTabletPortrait.value
})

const xMagicWallFeedbackPopup = computed<boolean>(() => {
  return !isMobileLayout.value && isWallCreatedWithAi.value
})

// Methods
function handlePageScroll(): void {
  if (!pageScrollHandlerTicking.value && !isMap.value) {
    window.requestAnimationFrame(() => {
      pageScrollHandlerTicking.value = false

      // Set header underneath background top position to hide it when the page scrolls the header and wishlist away
      const wallContainerEl = wallContainerMainElementRef.value || document.querySelector('#wall-container')
      if (wallContainerEl != null) {
        headerBackgroundTop.value = -wallContainerEl.scrollTop
      }

      // Search bar is 16px from the top
      // When in edit mode, we keep it fixed so users can scroll to see the posts while searching
      const searchBarTop = SEARCH_BAR_DEFAULT_TOP + (isSearchBarInEditMode.value ? 0 : headerBackgroundTop.value)
      useSurfaceStore().updateSearchBarTop(searchBarTop)
    })

    pageScrollHandlerTicking.value = true
  }
}

async function loadMap(): Promise<void> {
  if (!isMapApiLoaded.value) {
    await loadMapApi(googleApiKeyForMap.value, {
      region: currentCountryCode.value,
    })
    isMapApiLoaded.value = true
  }
}

async function handleFormatChange(): Promise<void> {
  /**
   * Styles for `grid` format (with or without sections) live entirely within Vue components.
   * We don't want to set `data-layout` to `shelf` since it will use old styles and mess up the view.
   */
  if (format.value === 'map') {
    await loadMap()
  }
  if (isSectionBreakout.value) {
    document.documentElement.setAttribute('data-layout', format.value === 'map' ? 'map' : 'grid')
    return
  }
  document.documentElement.setAttribute('data-layout', format.value === 'shelf' ? 'grid' : format.value)
}

function enableNewPostHandlers(): void {
  window.addEventListener('paste', handleOnPaste as (e: Event) => void)
}

function disableNewPostHandlers(): void {
  window.removeEventListener('paste', handleOnPaste as (e: Event) => void)
}

function refreshContentIfVisible() {
  if (document.visibilityState === 'visible') {
    refresh({ isAutoRefresh: true })
  }
}

function handleOnPaste(event: ClipboardEvent): void {
  event.stopPropagation()
  if (!canIPost.value) return

  if (isEventOnAnInputField(event)) {
    return
  }

  let data = extractPostAttributesArrayFromClipboardData(event.clipboardData)

  // When `shouldHandlePasteToAddAttachment` is `false`, we don't handle `PasteType.File`
  // and don't set the `attachment` field in `postAttributes` as it will add the attachment to the draft
  if (!shouldHandlePasteToAddAttachment.value) {
    data = removeAttachmentFromPostAttributes(data)
  }

  if (data.length === 0) return

  // Pasting texts from some applications like Microsoft Word may also paste an image version of the text
  // we want to treat them as text
  const isRichTextPastedAsFile =
    event.clipboardData?.types?.includes('text/rtf') && event.clipboardData?.types?.includes('Files')

  // Create a post with attachment without filename
  // when PasteType is File
  const attachment = extractPostAttributesFromClipboardData(data, PasteType.File)

  if (attachment && !isRichTextPastedAsFile) {
    startNewDraft({
      attributes: attachment.postAttributes,
      shouldStartEditing: true,
    })

    trackEvent('Posts', `Added draft by pasting ${attachment.pasteType}`)
    return
  }

  // Create a new post when PasteType is Link or Text or PostContentHash
  const dataWithoutFile = data.filter((datum) => {
    return datum?.pasteType !== PasteType.File
  })

  const isEmptyPost = dataWithoutFile[0] == null || isEmpty(dataWithoutFile[0].postAttributes)
  if (isEmptyPost) return

  startNewDraft({
    attributes: dataWithoutFile[0].postAttributes,
    shouldStartEditing: true,
  })
  trackEvent('Posts', `Added draft by pasting ${dataWithoutFile[0].pasteType}`)
}

function handleOnKeydownC(event: KeyboardEvent): void {
  if (!canIPost.value) return
  if (postBeingEditedCid.value) return
  if (isOverlayVisible.value) return
  if (isActiveElementAnInputField()) return
  event.preventDefault()

  if (isMap.value) {
    trackEvent('Keyboard Shortcuts', 'Triggered shortcut', null, null, {
      target: 'surface-shortcut-key-c',
      action: 'pick location',
    })
    startPickingLocation()
  } else {
    trackEvent('Keyboard Shortcuts', 'Triggered shortcut', null, null, {
      target: 'surface-shortcut-key-c',
      action: 'start new post',
    })
    startNewPost({})
  }
}

function handleOnKeydownModF(event: KeyboardEvent): void {
  if (postBeingEditedCid.value) return
  if (isOverlayVisible.value) return
  if (isActiveElementAnInputField()) return
  event.preventDefault()
  // Couldn't get Vue ref to work so we will use direct DOM APIs here.
  // TODO: Use Vue ref instead.
  document.getElementById('surface-search-bar')?.querySelector('input')?.select()
  useSurfaceStore().updateSearchBarTop(SEARCH_BAR_DEFAULT_TOP)
  trackEvent('Surface search', 'Used Ctrl F to search')
}

// Watcher
watch(isOnline, () => {
  refresh({ isAutoRefresh: true })
})

watch(canIPost, (canIPost) => {
  if (canIPost) {
    enableNewPostHandlers()
  } else {
    disableNewPostHandlers()
  }
})

watch(format, () => {
  handleFormatChange()
})

watch(
  reactionIdsByPost,
  (newValue: Record<Id, Id[]>) => {
    stableSync(reactionIdsByPostId.value, newValue)
  },
  { immediate: true },
)

watch(
  commentIdsByPost,
  (newValue) => {
    stableSync(commentIdsByPostId.value, newValue as Record<Id, Id[]>)
  },
  { immediate: true },
)

watch(isSearchBarInEditMode, () => {
  handlePageScroll()
})
// End watcher

let removeKeyboardShortcutsListener = (): void => {}

// Lifecycle hooks
onMounted(async () => {
  refresh({ isAutoRefresh: false })

  isWallCreatedWithAi.value = safeStorage.getItem('createdWithAi') === 'true'
  safeStorage.removeItem('createdWithAi')

  // Watch for size changes in surface container and update surface container size store
  observeSurfaceContainerSize(wallContainerMainElementRef.value as HTMLElement)
  // and content picker container size store
  observeContainerSize(wallContainerMainElementRef.value as HTMLElement)

  // Ensure new posts are loaded. Realtime might be disconnected when invisible, e.g. on mobile.
  document.addEventListener('visibilitychange', refreshContentIfVisible)

  if (isMap.value) {
    await loadMap()
  }
  nextTick(() => {
    if (canIPost.value) {
      enableNewPostHandlers()
    }
  })
  if (!xHeader.value) {
    // mobile app with native header
    resizeHeader({ height: 0 })
  }

  // Ensure that export as image captures all posts
  if (isScreenshotMode.value && isCanvas.value) {
    const hookToAllStoreChanges = ((store) => store?.subscribe.bind(store))(window.app?.$store)
    const events = ['load', 'beethoven-load', 'error', 'beethoven-error', hookToAllStoreChanges ?? '']
    const extraMarginInPx = 200
    subscribeUpdateBodySizeCss('wish', events, extraMarginInPx)
  }
  if (isSubscriptionPaused.value && amIOwner.value) {
    const snackbarData: GlobalSnackbarNotification = {
      message: __('Your subscription is paused. Your padlets are read-only until it resumes.'),
      persist: true,
    }
    if (!isApp) {
      snackbarData.actionText = __('Go to Settings')
      snackbarData.actionTextActions = [() => navigateTo('/dashboard/settings/billing')]
    }
    setSnackbar(snackbarData)
  }

  const shortcuts: KeyBindingMap = {
    KeyC: handleOnKeydownC,
    '$mod+KeyF': handleOnKeydownModF,
  }
  if (isAppUsing('keyboardShortcutsSetting') && user.value.is_keyboard_shortcuts_enabled === false) {
    delete shortcuts.KeyC
  }

  removeKeyboardShortcutsListener = tinykeys(window, shortcuts)

  if (isNavigatedFromSubmissionRequest() && !isSubmissionRequest.value) {
    showPostPublishedSnackbar()
  }
})

onBeforeUnmount(() => {
  disableNewPostHandlers()
  unsubscribeUpdateBodySizeCss()
  removeKeyboardShortcutsListener()
  document.removeEventListener('visibilitychange', refreshContentIfVisible)
})
// END LIFECYCLE HOOKS

// On setup
function onSetup(): void {
  if (!device.mobile) {
    useDoubleClickToPost()
  }
  useSurfaceFetchInitialState()
  if (device.app) {
    useNativeBridgeListeners({ for: 'surface' })
  }
}
onSetup()

return { __sfc: true,SurfaceActionBar, SurfaceGuestIdLabel, SurfaceContainerHeaderPad, SurfaceContainerGridMasonry, SurfaceContainerGrid, SurfaceContainerMap, SurfaceContainerTimelineOld, SurfaceContainerTimeline, SurfaceContainerMatrix, SurfaceContainerStream, SurfaceContainerCanvas, SurfaceContainerSectionBreakout, SurfaceContainerSubmissionRequest, SurfaceAddPost, SurfacePanels, SurfaceMagicWallFeedbackPopup, isMapApiLoaded, isApp, wallContainerMainElementRef, reactionIdsByPostId, commentIdsByPostId, headerBackgroundTop, pageScrollHandlerTicking, isWallCreatedWithAi, googleApiKeyForMap, currentCountryCode, amIOwner, canIPost, amIRegistered, surfaceStore, isEmbedded, isOnline, format, isOverlayVisible, isCanvas, isGrid, isMap, isScreenshotMode, isStream, isTable, isTimeline, isTimelineV1, isTimelineV2, isMatrix, isMobileLayout, isDesktopLayout, xHeader, isSubscriptionPaused, canUseSections, headerHeight, headerColor, isSectionBreakout, isSubmissionRequest, isFrozen, hasSidePanelOutsideSurface, user, isSearchBarInEditMode, refresh, resizeHeader, isContainerSmallerThanTabletLandscape, isContainerSmallerThanTabletPortrait, sufacePostsStore, postBeingEditedCid, startNewPost, wishGroupBy, reactionIdsByPost, commentIdsByPost, setSnackbar, startPickingLocation, startNewDraft, showPostPublishedSnackbar, observeSurfaceContainerSize, observeContainerSize, shouldHandlePasteToAddAttachment, setIsActionBarMounted, isSidePanelDesktop, xMagicWallFeedbackPopup, handlePageScroll, loadMap, handleFormatChange, enableNewPostHandlers, disableNewPostHandlers, refreshContentIfVisible, handleOnPaste, handleOnKeydownC, handleOnKeydownModF, removeKeyboardShortcutsListener, onSetup, isAppUsing, GroupByTypes }
}

})